import * as yup from 'yup';
import type {
    Contest,
    ContestContent,
    ContestContentTranslation,
} from '../../../stores/contest';
import { Language, LANGUAGES } from '../general-tab/form';

/** One language as edited: an empty text is absent. */
export interface ContentTranslationForm {
    detailImageUrlQc: string | null;
    detailImageUrlCa: string | null;
    confirmationTitle: string;
    confirmationBody: string;
    endedTitle: string;
    endedBody: string;
    endedImageUrl: string | null;
    faviconUrl: string | null;
    game: {
        image: string | null;
        detailImage: string | null;
        subTitle: string;
        description: string;
        buttonTitle: string;
    };
}

/** The images edited together: the banner is Couche-Tard's and Circle K's. */
export const IMAGE_SLOTS = {
    tile: ['game.image'],
    screen: ['game.detailImage'],
    banner: ['detailImageUrlQc', 'detailImageUrlCa'],
    ended: ['endedImageUrl'],
    favicon: ['faviconUrl'],
} as const;
export type ImageSlot = keyof typeof IMAGE_SLOTS;
type ImageField = typeof IMAGE_SLOTS[ImageSlot][number];

/**
 * With `sameBannerForCircleK`, Circle K shows the Couche-Tard banner in both
 * languages and its own images are hidden. An image slot in
 * `sameImageInBothLanguages` shows the English image in French too, and its
 * French images are hidden.
 */
export interface ContentFormValues {
    translations: Record<Language, ContentTranslationForm>;
    sameBannerForCircleK: boolean;
    sameImageInBothLanguages: Record<ImageSlot, boolean>;
}

function imageOf(
    translation: ContentTranslationForm,
    field: ImageField
): string | null {
    switch (field) {
        case 'game.image':
            return translation.game.image;
        case 'game.detailImage':
            return translation.game.detailImage;
        default:
            return translation[field];
    }
}

function withImage(
    translation: ContentTranslationForm,
    field: ImageField,
    url: string | null
): ContentTranslationForm {
    switch (field) {
        case 'game.image':
            return {
                ...translation,
                game: { ...translation.game, image: url },
            };
        case 'game.detailImage':
            return {
                ...translation,
                game: { ...translation.game, detailImage: url },
            };
        default:
            return { ...translation, [field]: url };
    }
}

function copyImages(
    from: ContentTranslationForm,
    to: ContentTranslationForm,
    slot: ImageSlot
): ContentTranslationForm {
    return IMAGE_SLOTS[slot].reduce(
        (translation, field) =>
            withImage(translation, field, imageOf(from, field)),
        to
    );
}

/** The French translation with the English images of the slot. */
export function withEnglishImages(
    values: ContentFormValues,
    slot: ImageSlot
): ContentTranslationForm {
    return copyImages(values.translations.en, values.translations.fr, slot);
}

/** Every slot is shared until one language has an image of its own. */
function sameImages(
    translations: Record<Language, ContentTranslationForm>
): Record<ImageSlot, boolean> {
    const same = (slot: ImageSlot) =>
        IMAGE_SLOTS[slot].every(
            (field) =>
                imageOf(translations.en, field) ===
                imageOf(translations.fr, field)
        );
    return {
        tile: same('tile'),
        screen: same('screen'),
        banner: same('banner'),
        ended: same('ended'),
        favicon: same('favicon'),
    };
}

function toTranslationForm(
    translation: ContestContentTranslation | undefined
): ContentTranslationForm {
    return {
        detailImageUrlQc: translation?.detailImageUrlQc ?? null,
        detailImageUrlCa: translation?.detailImageUrlCa ?? null,
        confirmationTitle: translation?.confirmationTitle ?? '',
        confirmationBody: translation?.confirmationBody ?? '',
        endedTitle: translation?.endedTitle ?? '',
        endedBody: translation?.endedBody ?? '',
        endedImageUrl: translation?.endedImageUrl ?? null,
        faviconUrl: translation?.faviconUrl ?? null,
        game: {
            image: translation?.game.image ?? null,
            detailImage: translation?.game.detailImage ?? null,
            subTitle: translation?.game.subTitle ?? '',
            description: translation?.game.description ?? '',
            buttonTitle: translation?.game.buttonTitle ?? '',
        },
    };
}

const EMPTY_TRANSLATIONS = {
    en: toTranslationForm(undefined),
    fr: toTranslationForm(undefined),
};

/** A contest just created has no copy and no image. */
export const EMPTY_CONTENT_VALUES: ContentFormValues = {
    translations: EMPTY_TRANSLATIONS,
    sameBannerForCircleK: true,
    sameImageInBothLanguages: sameImages(EMPTY_TRANSLATIONS),
};

export function toContentFormValues(contest: Contest): ContentFormValues {
    const translation = (lang: Language) =>
        toTranslationForm(
            contest.content.translations.find((each) => each.lang === lang)
        );
    const translations = { en: translation('en'), fr: translation('fr') };
    return {
        translations,
        sameBannerForCircleK: contest.content.translations.every(
            (each) => !each.detailImageUrlCa
        ),
        sameImageInBothLanguages: sameImages(translations),
    };
}

/** Blank is absent. */
function text(value: string): string | null {
    return value.trim() || null;
}

export function toContent(values: ContentFormValues): ContestContent {
    const french = (Object.keys(IMAGE_SLOTS) as ImageSlot[])
        .filter((slot) => values.sameImageInBothLanguages[slot])
        .reduce(
            (translation, slot) =>
                copyImages(values.translations.en, translation, slot),
            values.translations.fr
        );
    const translations = { en: values.translations.en, fr: french };
    return {
        translations: LANGUAGES.map((lang) => {
            const form = translations[lang];
            return {
                lang,
                detailImageUrlQc: form.detailImageUrlQc,
                detailImageUrlCa: values.sameBannerForCircleK
                    ? null
                    : form.detailImageUrlCa,
                confirmationTitle: text(form.confirmationTitle),
                confirmationBody: text(form.confirmationBody),
                endedTitle: text(form.endedTitle),
                endedBody: text(form.endedBody),
                endedImageUrl: form.endedImageUrl,
                faviconUrl: form.faviconUrl,
                game: {
                    image: form.game.image,
                    detailImage: form.game.detailImage,
                    subTitle: text(form.game.subTitle),
                    description: text(form.game.description),
                    buttonTitle: text(form.game.buttonTitle),
                },
            };
        }),
    };
}

const translationSchema = yup.object({
    confirmationTitle: yup.string().trim().max(150),
    endedTitle: yup.string().trim().max(150),
    game: yup.object({
        subTitle: yup.string().trim().max(255),
        buttonTitle: yup.string().trim().max(255),
    }),
});

/**
 * Every text and image may be left empty while the contest is a draft. The
 * API applies the same lengths; these show them before a round trip.
 */
export function contentFormSchema() {
    return yup.object({
        translations: yup.object({
            en: translationSchema,
            fr: translationSchema,
        }),
    });
}
